import React from 'react';
import styled from 'styled-components';

import DashedCheckMark from 'components/Icons/DashedCheckMark';

import { Eyebrow, StepsSubtitle, Title } from './headings';

const FEATURES = [
  { label: 'Your AI coach', description: 'One thing to do, every day.' },
  { label: 'WellScore', description: 'Your week in one number.' },
  {
    label: 'NutriScore',
    description: 'Macros, vitamins and minerals, scored.',
  },
  { label: 'Food diary', description: 'Meals in a tap.' },
  {
    label: 'Sleep, steps, water & weight',
    description: 'Logged in a tap, or synced from your watch.',
  },
  {
    label: 'Meal plans & shopping lists',
    description: 'By Shido’s nutritionists, matched to you.',
  },
  { label: 'Health assessments', description: 'Know where you stand first.' },
  {
    label: 'Courses',
    description: 'From Shido’s experts, ten minutes a day.',
  },
  { label: 'Guided meditations', description: 'For sleep, stress and focus.' },
  { label: 'Workout videos', description: 'At home, any level.' },
  { label: 'Podcasts', description: 'Shido’s experts, in your ears.' },
  { label: 'Ask an expert', description: 'A real person when you want one.' },
];

type Props = {
  subtitle?: string;
};

const MembershipSection = ({ subtitle }: Props) => (
  <Container>
    <Eyebrow>Your membership</Eyebrow>
    <MembershipTitle>Everything’s included.</MembershipTitle>
    {subtitle && <MembershipSubtitle>{subtitle}</MembershipSubtitle>}
    <List aria-label="Included in your membership">
      {FEATURES.map(({ label, description }) => (
        <Row key={label}>
          <Label>{label}</Label>
          <Description>{description}</Description>
          <Check aria-hidden>
            <DashedCheckMark width={20} height={20} />
          </Check>
        </Row>
      ))}
    </List>
  </Container>
);

const Container = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 80px;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    padding-top: 40px;
  }
`;

const MembershipTitle = styled(Title)`
  margin-bottom: 8px;
`;

const MembershipSubtitle = styled(StepsSubtitle)`
  ${({ theme }) => theme.breakpoints.down('md')} {
    padding: 0 20px;
  }
`;

const List = styled.ul`
  box-sizing: border-box;
  width: 704px;
  max-width: 100%;
  margin: 40px 0 0;
  padding: 0;
  list-style: none;
  border-top: 1px solid rgb(65 78 113 / 15%);

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-width: calc(100% - 40px);
    margin-top: 32px;
  }
`;

const Row = styled.li`
  display: grid;
  grid-template-columns: 34% 1fr 80px;
  align-items: start;
  min-height: 51px;
  border-bottom: 1px solid rgb(65 78 113 / 15%);
  color: ${({ theme }) => theme.colors.topicBlue};

  ${({ theme }) => theme.breakpoints.down('md')} {
    grid-template-columns: 1fr 48px;
  }
`;

const Label = styled.span`
  padding: 13px 24px 13px 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: inherit;

  ${({ theme }) => theme.breakpoints.down('md')} {
    padding: 12px 12px 0 0;
    font-size: 15px;
    line-height: 22px;
  }
`;

const Description = styled.span`
  padding: 13px 16px 13px 0;
  font-size: 16px;
  line-height: 24px;
  color: inherit;

  ${({ theme }) => theme.breakpoints.down('md')} {
    grid-row: 2;
    padding: 2px 12px 12px 0;
    font-size: 14px;
    line-height: 20px;
    opacity: 0.8;
  }
`;

const Check = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  background-color: ${({ theme }) => theme.colors.white};

  ${({ theme }) => theme.breakpoints.down('md')} {
    grid-column: 2;
    grid-row: 1 / span 2;
  }
`;

export default MembershipSection;
